import React from 'react';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { Nav, Badge } from 'reactstrap';
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from 'perfect-scrollbar';
import logo from 'logo-white.svg';
import { PermissionContext } from '../../contexts';
import approvals from '../../services/approvals.service';
import { setPendingApprovalsCount } from '../../actions/approvalActions';
import { NAV_GROUPS, EXPANDED_GROUP_KEY } from '../../routes/navGroups';

var ps;

const BADGE_POLL_INTERVAL = 30000;
const BADGE_SCAN_LIMIT = 100;

// The route whose path matches the first segment of the current URL, e.g.
// /orders/1234 -> the /orders route. Mirrors activeRoute() below.
const routeForPath = (routes, pathname) => {
  const section = `/${pathname.split('/')[1]}`;
  return routes.find((r) => r.path === section);
};

class Sidebar extends React.Component {
  static contextType = PermissionContext;

  constructor(props) {
    super(props);
    this.activeRoute.bind(this);

    // Open the group containing the current page, so a deep link or a refresh
    // never lands the user in a sidebar where nothing is expanded. Falls back
    // to whatever they last had open.
    const active = routeForPath(props.routes, props.location.pathname);
    let persisted = null;
    try {
      persisted = localStorage.getItem(EXPANDED_GROUP_KEY);
    } catch (e) {
      /* private mode / storage disabled — just start collapsed */
    }

    this.state = { expanded: (active && active.group) || persisted || null };
  }

  sidebarRef = React.createRef();

  // Keep the "Approvals" pending badge live. Best-effort: if the user isn't
  // authorised the request fails silently and no badge/polling is set up.
  // The queue is keyset-paginated with no total, so this counts one capped page
  // of pending requests: the badge is a floor ("at least this many"), which is
  // all a nudge needs to be.
  refreshPendingCount = () => {
    approvals
      .list({
        where: { status: 'pending' },
        limit: BADGE_SCAN_LIMIT,
        // The sidebar outlives every page, so its poll opts out of the
        // navigation-abort policy that page requests get by default.
        abortOnNavigate: false,
      })
      .then((res) => {
        const items = res.data || [];
        const actionable = items.filter((r) => r.can_check).length;
        this.props.dispatch(
          setPendingApprovalsCount(actionable || items.length)
        );
        if (!this.badgeTimer) {
          this.badgeTimer = setInterval(
            this.refreshPendingCount,
            BADGE_POLL_INTERVAL
          );
        }
      })
      .catch(() => {
        /* not authorised or transient error — leave badge at 0 */
      });
  };

  // verifies if routeName is the one active (in browser input)
  activeRoute(path) {
    const splits = this.props.location.pathname.split('/');
    return `/${splits[1]}` === path ? 'active' : '';
  }

  // Single-expand accordion: opening one group closes the others. Clicking the
  // open group collapses it, even when it holds the current page.
  toggleGroup = (key) => {
    this.setState(
      (prev) => ({ expanded: prev.expanded === key ? null : key }),
      () => {
        try {
          if (this.state.expanded) {
            localStorage.setItem(EXPANDED_GROUP_KEY, this.state.expanded);
          } else {
            localStorage.removeItem(EXPANDED_GROUP_KEY);
          }
        } catch (e) {
          /* storage unavailable — expansion just won't survive a reload */
        }
      }
    );
  };

  componentDidMount() {
    if (navigator.platform.indexOf('Win') > -1) {
      ps = new PerfectScrollbar(this.sidebarRef, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    this.refreshPendingCount();
  }

  componentDidUpdate(prevProps) {
    // Navigating into a collapsed group (via a link, the browser back button,
    // or a redirect) should reveal where you are.
    if (prevProps.location.pathname === this.props.location.pathname) return;

    const active = routeForPath(
      this.props.routes,
      this.props.location.pathname
    );
    if (active && active.group && active.group !== this.state.expanded) {
      this.toggleGroup(active.group);
    }
  }

  componentWillUnmount() {
    if (navigator.platform.indexOf('Win') > -1) {
      ps.destroy();
    }
    if (this.badgeTimer) clearInterval(this.badgeTimer);
  }

  renderItem = (prop, isGrouped) => (
    <li
      className={
        this.activeRoute(prop.path) +
        (isGrouped ? ' nav-group-item' : '') +
        (prop.pro ? ' active active-pro' : '')
      }
      key={prop.path}
    >
      <NavLink
        exact
        strict
        to={prop.path}
        className="nav-link"
        activeClassName="active"
        isActive={(a, b) => {
          const splits = b.pathname.split('/');
          return `/${splits[1]}` === prop.path;
        }}
      >
        {!isGrouped && <i className={'now-ui-icons ' + prop.icon} />}
        <p>
          {prop.name}
          {prop.badge === 'approvals' && this.props.pendingApprovals > 0 && (
            <Badge color="danger" pill className="ml-2">
              {this.props.pendingApprovals}
            </Badge>
          )}
        </p>
      </NavLink>
    </li>
  );

  render() {
    const { hasPermission } = this.context;

    // Routes are ordered by group (see routes/navGroups.js) rather than sorted
    // alphabetically, so related screens sit together.
    const visible = this.props.routes.filter(
      (r) => !r.redirect && !r.hidden && hasPermission(r.resource, r.action)
    );

    // Pinned items sort by `navOrder` rather than array position: `/` has to be
    // declared last in routes/index.js so it stays Main.js's catch-all Route,
    // but it belongs at the top of the nav.
    const byNavOrder = (a, b) => (a.navOrder || 0) - (b.navOrder || 0);
    const pinnedTop = visible
      .filter((r) => r.pinned === 'top')
      .sort(byNavOrder);
    const pinnedBottom = visible
      .filter((r) => r.pinned === 'bottom')
      .sort(byNavOrder);

    // A group with no visible children would render as a dead header, which is
    // worse than the flat list it replaced — permissions hide most items for
    // most users, so drop empty groups entirely.
    const groups = NAV_GROUPS.map((g) => ({
      ...g,
      items: visible.filter((r) => r.group === g.key),
    })).filter((g) => g.items.length > 0);

    // Safety net: a route with neither `group` nor `pinned` still shows up
    // rather than silently disappearing from the nav.
    const known = new Set(NAV_GROUPS.map((g) => g.key));
    const ungrouped = visible.filter(
      (r) => !r.pinned && (!r.group || !known.has(r.group))
    );

    // The section holding the current page — which is not necessarily the
    // expanded one, since you can browse other sections without navigating.
    const active = routeForPath(visible, this.props.location.pathname);
    const activeGroup = active && active.group;

    return (
      <div className="sidebar" data-color="blue">
        <div className="logo">
          <a
            href="https://iadmin.buypower.ng"
            className="simple-text logo-mini"
          >
            <div className="logo-img">
              <img src={logo} alt="react-logo" />
            </div>
          </a>
          <a
            href="https://iadmin.buypower.ng"
            className="simple-text logo-normal"
          >
            BUYPOWER
          </a>
        </div>
        <div className="sidebar-wrapper" ref={(el) => (this.sidebarRef = el)}>
          <Nav>
            {pinnedTop.map((prop) => this.renderItem(prop, false))}

            {groups.map((group) => {
              const open = this.state.expanded === group.key;

              return (
                <React.Fragment key={group.key}>
                  <li className="nav-group">
                    <button
                      type="button"
                      className={
                        'nav-group-toggle' +
                        (activeGroup === group.key ? ' is-active' : '')
                      }
                      aria-expanded={open}
                      onClick={() => this.toggleGroup(group.key)}
                    >
                      <i className={'now-ui-icons ' + group.icon} />
                      <p>{group.label}</p>
                      <i
                        className={
                          'now-ui-icons nav-group-caret ' +
                          (open
                            ? 'arrows-1_minimal-down'
                            : 'arrows-1_minimal-right')
                        }
                      />
                    </button>
                  </li>
                  {open &&
                    group.items.map((prop) => this.renderItem(prop, true))}
                </React.Fragment>
              );
            })}

            {ungrouped.map((prop) => this.renderItem(prop, false))}
            {pinnedBottom.map((prop) => this.renderItem(prop, false))}
          </Nav>
        </div>
      </div>
    );
  }
}

export default connect((state) => ({
  pendingApprovals: state.approvals.pendingCount,
}))(Sidebar);
